import { PledgeValidator } from "./pledge-validator";

export class PledgeModule {
  initialize() {
    if (!this.form) return;

    this.addEventListeners();
    this.render();
  }

  // Element getters
  get form() {
    return document.querySelector(".pledge-form");
  }

  get stepper() {
    return this.form.querySelector(".pledge-form-stepper");
  }

  get steps() {
    return this.form.querySelectorAll(".pledge-form-step");
  }

  get backButton() {
    return this.form.querySelector(".pledge-form-button-back");
  }

  get nextButton() {
    return this.form.querySelector(".pledge-form-button-next");
  }

  get progress() {
    return this.form.querySelector(".pledge-form-progress");
  }

  get progressCount() {
    return this.progress.querySelector(".pledge-form-progress-count");
  }

  get progressMinCount() {
    return this.progress.querySelector(".pledge-form-progress-min");
  }

  get pledgeItems() {
    return this.form.querySelectorAll(".pledge-form-item");
  }

  get domainItems() {
    return this.form.querySelectorAll(".pledge-form-domain-item");
  }

  get currentStep() {
    return parseInt(this.form.dataset.currentStep);
  }

  set currentStep(step) {
    if (step > this.currentStep) {
      const pledgeValidator = new PledgeValidator(
        this.form,
        this.currentStep,
        this
      );
      if (!pledgeValidator.validate()) return;
    }

    this.form.dataset.currentStep = step;
    this.render();
  }

  get checkedDomainItems() {
    return Array.from(this.domainItems).filter((item) => item.checked);
  }

  get checkedPledgeItems() {
    return Array.from(this.pledgeItems).filter((item) => item.checked);
  }

  get maxStep() {
    return parseInt(this.form.dataset.maxStep);
  }

  get isLastStep() {
    return this.currentStep === this.maxStep;
  }

  get minimumPledges() {
    return parseInt(this.form.dataset.minimumPledges);
  }

  // Event handlers
  addEventListeners() {
    this.backButton.addEventListener("click", this.handleBackButtonClick.bind(this));
    this.nextButton.addEventListener("click", this.handleNextButtonClick.bind(this));
    this.pledgeItems.forEach((item) => {
      item.addEventListener("click", this.handlePledgeClick.bind(this));
    });
  }

  handleBackButtonClick(e) {
    e.preventDefault();
    if (this.currentStep === 1) return;
    this.currentStep = this.currentStep - 1;
  }

  handleNextButtonClick(e) {
    e.preventDefault();

    // If the last step is reached, validate the step and submit the form
    // Otherwise, up the current step
    if (this.isLastStep) {
      if (this.validateStep(this.currentStep)) {
        this.form.submit();
      }

      return;
    }

    this.currentStep += 1;
  }

  handlePledgeClick() {
    this.render();
  }

  validateStep(step) {
    const validator = new PledgeValidator(this.form, step, this);
    return validator.validate();
  }
  
  // Render methods
  updateBackButton() {
    // Toggle the visibility of the back button based on the current step
    this.backButton.classList.toggle("pledge-form-button-visible", this.currentStep > 1);
  }

  updateNextButton() {
    // Determine the remaining pledge items to select to enable the next button
    const remaining = this.minimumPledges - this.checkedPledgeItems.length;

    // Determine if the next button should be primary and disabled
    const isPrimaryButton = this.isLastStep ? remaining <= 0 : true;
    const isDisabled = this.isLastStep ? remaining > 0: false;

    // Determine the tooltip for the next button
    const tooltip = this.isLastStep && remaining > 0 ? `Selecteer nog ${remaining} belofte${remaining > 1 ? "s" : ""}` : null;

    // Update the button state and text
    this.setButtonState(this.nextButton, isPrimaryButton, isDisabled, tooltip);
    this.nextButton.innerHTML = this.isLastStep ? "Ondertekenen" : "Volgende";
  }

  setButtonState(button, isPrimary, isDisabled, tooltip = "") {
    // Update the button classes and tooltip based on the state
    button.classList.toggle("btn-primary", isPrimary);
    button.classList.toggle("btn-outline-gray-100", !isPrimary);
    button.classList.toggle("btn-gray-300", !isPrimary);
    button.style.cursor = isDisabled ? "not-allowed" : "";

    this.updateButtonTooltip(button, tooltip);
  }

  updateButtonTooltip(button, tooltip) {
    // Update the button tooltip if it exists, otherwise remove it
    if (tooltip) button.dataset.tooltip = tooltip;
    else button.removeAttribute("data-tooltip");
  }

  renderStepper() {
    // Update the stepper for each step
    const stepElements = this.stepper.querySelectorAll(".stepper-2a-step");

    stepElements.forEach((step, index) => {
      // Determine the classes for the current and active steps
      step.classList.toggle("stepper-2a-step--active", index + 1 <= this.currentStep);
      step.classList.toggle("stepper-2a-step--current", index + 1 === this.currentStep);
    });
  }

  renderStep() {
    this.steps.forEach((step) => {
      step.classList.toggle(
        "pledge-form-step-visible",
        parseInt(step.dataset.step) === this.currentStep
      );
    });
  }

  renderButtons() {
    this.updateBackButton();
    this.updateNextButton();
  }

  renderProgress() {
    // Update the progress bar based on the number of selected pledge items
    this.progress.classList.toggle("text-gray-100", this.checkedPledgeItems.length < this.minimumPledges);
    this.progress.classList.toggle("text-primary", this.checkedPledgeItems.length >= this.minimumPledges);
    this.progressCount.innerHTML = this.checkedPledgeItems.length;
    this.progressMinCount.innerHTML = this.minimumPledges;
  }

  render() {
    this.renderStepper();
    this.renderStep();
    this.renderButtons();
    this.renderProgress();
  }
}
